import React, { Component } from 'react';
import { isEmpty, isEqual, isNil } from 'lodash';
import Overlay from 'src/common-ui/components/Overlay/Overlay';
import styleEditStyles from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.styles';
import { generateStylePreview } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import Subheader from 'src/components/Subheader/Subheader.container';
import { formatSummaries } from 'src/utils/Pivot/RollUp';
import CompanionList from 'src/components/CompanionList/CompanionList';
import type { StyleEditProps, StyleEditState } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.types';
import StylePreview from 'src/components/StylePreview/StylePreview';
import { FabType } from 'src/components/higherOrder/withFab';
import { parseCompanionListViewConfig } from 'src/utils/Component/ListView';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { companionDataParse } from 'src/components/ListGridPair/ListGridPair.utils';
import { NoSelectedCompanionItemOverlay } from 'src/components/NoSelectedCompanionItemOverlay/NoSelectedCompanionItemOverlay';

export default class StyleEdit extends Component<StyleEditProps, StyleEditState> {
  constructor(props: StyleEditProps) {
    super(props);

    this.state = {
      companionScrollTo: this.props.redirectSelection && {
        eventId: Date.now(),
        where: {
          key: 'id',
          value: this.props.redirectSelection,
        },
      },
      selectedCompanionId: this.props.redirectSelection?.id ?? null,
    };
  }

  componentDidMount() {
    this.props.onShowView();
  }

  componentDidUpdate(prevProps: StyleEditProps) {
    // `generateCompanionData` needs config to be present or else it will crash
    if (!prevProps.redirectSelection && this.props.redirectSelection && !isEmpty(this.props.config)) {
      const { companionData } = this.generateCompanionData();
      const selectedCompanionItem = companionData.find((s) => s.id === this.props.redirectSelection?.id);
      this.setState({
        selectedCompanionId: selectedCompanionItem?.id ?? null,
        companionScrollTo: {
          eventId: Date.now(),
          where: {
            key: 'id',
            value: this.props.redirectSelection.id,
          },
        },
      });
    }
  }

  componentWillUnmount(): void {
    this.props.onDestroy();
  }

  generateCompanionData = () => {
    const { config, styles, companionSortField, companionSortDirection } = this.props;
    const defaultSortDataIndex = config.companionView?.sortBy.defaults.dataIndex ?? '';
    const sortOptions = config.companionView?.sortBy.options ?? [];
    const fieldExists = !isNil(companionSortField) && sortOptions.some((opt) => opt.dataIndex === companionSortField);
    const effectiveSortField = fieldExists ? companionSortField! : defaultSortDataIndex;
    const companionDataLookup = parseCompanionListViewConfig(
      config.companionView.itemMappings as unknown as TenantConfigViewData,
    );
    const companionData = companionDataParse(styles, companionDataLookup, companionSortDirection, effectiveSortField);
    return {
      companionData,
      companionDataLookup,
    };
  };

  onFabClick = () => {
    switch (this.props.fab.fabType) {
      case FabType.planning:
        this.props.updateAssortmentPlan();
        break;
      default:
        break;
    }
  };

  handleItemSelect = (companionItemId: string) => {
    if (companionItemId === '') {
      this.setState({ selectedCompanionId: null });
      return;
    }
    if (isEqual(companionItemId, this.state.selectedCompanionId)) {
      this.setState(
        {
          selectedCompanionId: null,
        },
        () => {
          this.setState({
            selectedCompanionId: companionItemId,
            companionScrollTo: {
              eventId: Date.now(),
              where: {
                key: 'id',
                value: companionItemId,
              },
            },
          });
        },
      );
    } else {
      this.setState({
        selectedCompanionId: companionItemId,
        companionScrollTo: {
          eventId: Date.now(),
          where: {
            key: 'id',
            value: companionItemId,
          },
        },
      });
    }
  };

  render() {
    // config is required to render the rest of the component chain
    // so render only overlay until config is ready
    if (isEmpty(this.props.config) && this.props.isLoading) {
      return <Overlay type="loading" visible={true} qaKey="StyleEditOverlay" />;
    }

    const {
      title,
      isLoading,
      config,
      adornments,
      summary,
      companionFilters,
      previewFilters,
      flowStatusOptions,
      searchKeys,
      sortByDefn,
      isCompanionCollapsed,
      onSearchChange,
      onFilterChange,
      onShowView,
      onUpdate,
      onError,
      onToggleCompanion,
      companionSortField,
      companionSortDirection,
      updateCompanionSortField,
      updateCompanionSortDirection,
    } = this.props;
    const { companionScrollTo, selectedCompanionId } = this.state;
    const sortOptions = config.companionView?.sortBy.options ?? [];
    const defaultSortDataIndex = config.companionView?.sortBy.defaults.dataIndex ?? '';
    const sortField =
      !isNil(companionSortField) && sortOptions.some((opt) => opt.dataIndex === companionSortField)
        ? companionSortField
        : defaultSortDataIndex;

    const { companionData, companionDataLookup } = this.generateCompanionData();
    const selectedCompanionItem = companionData.find((item) => item.id === selectedCompanionId) ?? null;
    const stylePreviewData = selectedCompanionItem ? generateStylePreview(selectedCompanionItem) : null;
    const subheaderProps = {
      title,
      sortByDefn: !isNil(sortByDefn) ? sortByDefn : 'AssortmentBuildStyleEditSortBy',
      summary: formatSummaries(summary),
      showAlternateFlowStatus: true,
      showAlternateSearch: true,
      sortByRequired: true,
    };
    let mainViewComponent: JSX.Element;
    if (stylePreviewData) {
      mainViewComponent = (
        <StylePreview
          adornments={adornments}
          filters={previewFilters}
          searchKeys={searchKeys}
          previewData={stylePreviewData}
          renderSections={true}
          sectionsViewDefns={config.sectionsViewDefns}
          onRefreshView={onShowView}
          onStyleItemEdit={onUpdate}
          onError={onError}
        />
      );
    } else if (!isLoading) {
      // Show no selected if there is no selected item and view is finished loading
      mainViewComponent = <NoSelectedCompanionItemOverlay />;
    } else {
      // show empty div while view is loading for cleanliness
      mainViewComponent = <div />;
    }
    return (
      <div className={styleEditStyles.styleEditContainer}>
        <Overlay type="loading" visible={isLoading} />
        <Subheader {...subheaderProps} />
        <div className="data-container">
          <CompanionList
            isCompanionCollapsed={isCompanionCollapsed}
            config={config.companionView}
            selectedItemId={selectedCompanionId}
            data={companionData}
            dataLookup={companionDataLookup}
            isDataloaded={!isLoading}
            renderSearchComponent={true}
            renderFilterComponent={true}
            sortDirection={companionSortDirection}
            sortField={sortField}
            search={companionFilters.search}
            flowStatus={companionFilters.flowStatus}
            flowStatusOptions={flowStatusOptions}
            onSearchChange={onSearchChange}
            onFilterChange={onFilterChange}
            onSortChange={updateCompanionSortField}
            onSortDirectionChange={updateCompanionSortDirection}
            onItemSelect={this.handleItemSelect}
            onToggleCompanion={onToggleCompanion}
            scrollTo={companionScrollTo}
          />
          <div
            style={{ height: '100%', overflowX: 'auto', flexGrow: 1 }}
            id={'StyleEditStylePreviewScroller'}
            data-qa="StyleEditStylePreviewScroller"
          >
            {mainViewComponent}
          </div>
        </div>
      </div>
    );
  }
}
